import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  Edit, 
  Save, 
  X,
  Truck,
  Car,
  Package,
  Plus,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [newService, setNewService] = useState({
    title: '',
    price: '',
    description: '',
    category: 'tungbil'
  });
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste tjenester",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingId(service.id);
    setEditForm({
      title: service.title,
      price: service.price,
      description: service.description || '',
      category: service.category
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (serviceId) => {
    try {
      const updateData = {
        title: editForm.title,
        price: editForm.price,
        description: editForm.description,
        category: editForm.category
      };
      
      await axios.put(`${API}/services/${serviceId}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast({
        title: "Suksess! ✅",
        description: "Tjeneste oppdatert"
      });

      await fetchServices();
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating service:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke oppdatere tjeneste",
        variant: "destructive"
      });
    }
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'tungbil':
        return <Truck className="w-5 h-5" />;
      case 'tilhenger':
        return <Package className="w-5 h-5" />;
      case 'personbil':
        return <Car className="w-5 h-5" />;
      default:
        return <Truck className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (type) => {
    switch (type) {
      case 'tungbil':
        return 'bg-red-600/20 text-red-400 border-red-600/50';
      case 'tilhenger':
        return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/50';
      case 'personbil':
        return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
      default:
        return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
    }
  };

  const handleCreateService = async () => {
    if (!newService.title || !newService.price) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst fyll ut tittel og pris",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/services`, newService, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast({
        title: "Tjeneste Opprettet! ✅",
        description: "Ny tjeneste er lagt til"
      });

      setNewService({
        title: '',
        price: '',
        description: '',
        category: 'tungbil'
      });
      setIsCreateModalOpen(false);
      await fetchServices();
    } catch (error) {
      console.error('Error creating service:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke opprette tjeneste",
        variant: "destructive"
      });
    }
  };

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      await axios.delete(`${API}/services/${serviceToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast({
        title: "Tjeneste Slettet! ✅",
        description: "Tjenesten er fjernet"
      });

      setIsDeleteDialogOpen(false);
      setServiceToDelete(null);
      await fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette tjeneste",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-white text-xl">Laster tjenester...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Tjenester & Priser
            </span>
          </h2>
          <p className="text-gray-400">Administrer tjenester og prising</p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny Tjeneste
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {services.map((service) => (
          <Card 
            key={service.id} 
            className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700 backdrop-blur-sm hover:border-red-600/50 transition-all duration-300"
          >
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className={getCategoryColor(service.category || service.type)}>
                    {getCategoryIcon(service.category || service.type)}
                    <span className="ml-1 capitalize">{service.category || service.type}</span>
                  </Badge>
                </div>
                {editingId === service.id ? (
                  <div className="space-y-2">
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="Tjenestenavn"
                    />
                  </div>
                ) : (
                  <CardTitle className="text-white text-lg leading-tight">
                    {service.title}
                  </CardTitle>
                )}
              </div>
              {editingId === service.id ? (
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    onClick={() => handleSave(service.id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                    className="border-gray-600 text-gray-400 hover:bg-gray-800"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(service)}
                    className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/20"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setServiceToDelete(service);
                      setIsDeleteDialogOpen(true);
                    }}
                    className="border-red-600 text-red-400 hover:bg-red-600/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {editingId === service.id ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Pris</Label>
                    <Input
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="f.eks. 3338,-"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Beskrivelse (valgfritt)</Label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Tilleggsinformasjon om tjenesten..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Kategori</Label>
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    >
                      <option value="tungbil">Tungbil</option>
                      <option value="tilhenger">Tilhenger</option>
                      <option value="personbil">Personbil</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-3xl font-black text-white">{service.price}</span>
                  </div>
                  {service.description && (
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Service Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center space-x-2">
              <Plus className="w-6 h-6 text-green-400" />
              <span>Opprett Ny Tjeneste</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Fyll ut informasjonen under for å legge til en ny tjeneste
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Tittel *</Label>
              <Input
                value={newService.title}
                onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="F.eks. EU-kontroll person/varebil"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Pris *</Label>
              <Input
                value={newService.price}
                onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="F.eks. 960,-"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Kategori *</Label>
              <select
                value={newService.category}
                onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="tungbil">Tungbil</option>
                <option value="tilhenger">Tilhenger</option>
                <option value="personbil">Personbil</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Beskrivelse (valgfritt)</Label>
              <textarea
                value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Tilleggsinformasjon om tjenesten..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewService({
                    title: '',
                    price: '',
                    description: '',
                    category: 'tungbil'
                  });
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Avbryt
              </Button>
              <Button
                onClick={handleCreateService}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Opprett Tjeneste
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center space-x-2">
              <Trash2 className="w-6 h-6 text-red-400" />
              <span>Bekreft Sletting</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette denne tjenesten?
            </DialogDescription>
          </DialogHeader>

          {serviceToDelete && (
            <div className="bg-gray-800/50 rounded-lg p-4 my-4">
              <div className="text-white font-semibold mb-1">{serviceToDelete.title}</div>
              <div className="text-gray-400 text-sm">{serviceToDelete.price}</div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setServiceToDelete(null);
              }}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleDeleteService}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett Tjeneste
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
